import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    firstName: string;
    lastName: string;
    attention: string;
    companyName: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    country: BaseAddressModel["country"];
    state: BaseAddressModel["state"];
    city: string;
    postalCode: string;
    phone: string;
    email: string;
    fax: string;
    currentUserIsGuest: boolean | undefined;
    onChangeFirstName: TextFieldProps["onChange"];
    onChangeLastName: TextFieldProps["onChange"];
    onChangeAttention: TextFieldProps["onChange"];
    onChangeCompanyName: TextFieldProps["onChange"];
    onChangeAddress1: TextFieldProps["onChange"];
    onChangeAddress2: TextFieldProps["onChange"];
    onChangeAddress3: TextFieldProps["onChange"];
    onChangeAddress4: TextFieldProps["onChange"];
    onChangeCountry: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    onChangeState: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    onChangeCity: TextFieldProps["onChange"];
    onChangePostalCode: TextFieldProps["onChange"];
    onChangePhone: TextFieldProps["onChange"];
    onChangeEmail: TextFieldProps["onChange"];
    onChangeFax: TextFieldProps["onChange"];
    onBlurFirstName: TextFieldProps["onBlur"];
    onBlurLastName: TextFieldProps["onBlur"];
    onBlurAttention: TextFieldProps["onBlur"];
    onBlurCompanyName: TextFieldProps["onBlur"];
    onBlurAddress1: TextFieldProps["onBlur"];
    onBlurAddress2: TextFieldProps["onBlur"];
    onBlurAddress3: TextFieldProps["onBlur"];
    onBlurAddress4: TextFieldProps["onBlur"];
    onBlurCity: TextFieldProps["onBlur"];
    onBlurPostalCode: TextFieldProps["onBlur"];
    onBlurPhone: TextFieldProps["onBlur"];
    onBlurEmail: TextFieldProps["onBlur"];
    onBlurFax: TextFieldProps["onBlur"];
    firstNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    attentionError: React.ReactNode;
    companyNameError: React.ReactNode;
    address1Error: React.ReactNode;
    address2Error: React.ReactNode;
    address3Error: React.ReactNode;
    address4Error: React.ReactNode;
    countryError: React.ReactNode;
    stateError: React.ReactNode;
    cityError: React.ReactNode;
    postalCodeError: React.ReactNode;
    phoneError: React.ReactNode;
    emailError: React.ReactNode;
    faxError: React.ReactNode;
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    validation: CustomerValidationDto;
    extendedStyles?: CustomerAddressFormFieldStyles;
    hideAdditionalAddresses?: boolean;
    isShippingAddress?: boolean;
}

type Props = OwnProps;
export interface CustomerAddressFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    registeredUserAddress1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    registeredUserAddress2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    registeredUserCountryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    registeredUserCityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    registeredUserStateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    registeredUserPostalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    registeredUserPhoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    registeredUserEmailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    addAddressLineLinkGridItem?: GridItemProps;
    addAddressLineLink?: LinkPresentationProps;
    
}

export const customerAddressFormFieldStyles: CustomerAddressFormFieldStyles = {
    firstNameGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_firstName-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    lastNameGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_lastName-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    companyNameGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        label {
                            left: 10px;
                        }
                    `,
                    null,
                ])}
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_companyName-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    attentionGridItem: { width: [12, 12, 6, 6, 6] },
    address1GridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_address1-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },

    registeredUserAddress1GridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_address1-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    address2GridItem: { width: [12, 12, 12, 12, 12] },
    registeredUserAddress2GridItem: { width: [12, 12, 12, 12, 12] },
    address3GridItem: { width: 12 },
    address4GridItem: { width: 12 },
    addAddressLineLinkGridItem: { width: 12 },
    countryGridItem: { width: [12, 12, 6, 6, 6] },
    registeredUserCountryGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            order: 3;
        `,
    },
    cityGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_city-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    registeredUserCityGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_city-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_state-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    registeredUserStateGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_state-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    postalCodeGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_postalCode-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    registeredUserPostalCodeGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            order: 1;
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_postalCode-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    phoneGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_phone-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    registeredUserPhoneGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            order: 2;
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_phone-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    faxGridItem: { width: [12, 12, 6, 6, 6] },
    emailGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_email-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    registeredUserEmailGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            order: 4;
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        animation: shake 0.8s;
                        span[data-test-selector="customerAddressForm_email-error"] {
                            background: rgb(248, 220, 220);
                            font-size: 14px !important;
                            @keyframes shake {
                                0% {
                                    transform: translateX(0);
                                }
                                25% {
                                    transform: translateX(25px);
                                }

                                50% {
                                    transform: translateX(-25px);
                                }
                                100% {
                                    transform: translateX(0px);
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
   
};

// const styles = customerAddressFormFieldStyles;

const CustomerAddressFormFields = ({
    firstName,
    lastName,
    attention,
    companyName,
    address1,
    address2,
    address3,
    address4,
    country,
    state,
    city,
    postalCode,
    phone,
    email,
    fax,
    currentUserIsGuest,
    onChangeFirstName,
    onChangeLastName,
    onChangeAttention,
    onChangeCompanyName,
    onChangeAddress1,
    onChangeAddress2,
    onChangeAddress3,
    onChangeAddress4,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    onChangePostalCode,
    onChangePhone,
    onChangeEmail,
    onChangeFax,
    onBlurFirstName,
    onBlurLastName,
    onBlurAttention,
    onBlurCompanyName,
    onBlurAddress1,
    onBlurAddress2,
    onBlurAddress3,
    onBlurAddress4,
    onBlurCity,
    onBlurPostalCode,
    onBlurPhone,
    onBlurEmail,
    onBlurFax,
    firstNameError,
    lastNameError,
    attentionError,
    companyNameError,
    address1Error,
    address2Error,
    address3Error,
    address4Error,
    countryError,
    stateError,
    cityError,
    postalCodeError,
    phoneError,
    emailError,
    faxError,
    countries,
    fieldDisplay,
    validation,
    extendedStyles,
    hideAdditionalAddresses,
    isShippingAddress,
}: Props) =>
    // props: customProps,
    {
        const [isUsStates, setisUsStates] = React.useState(true);
        const [styles] = React.useState(() => mergeToNew(customerAddressFormFieldStyles, extendedStyles));

        if (!countries) {
            return null;
        }
        // const currentUserIsGuest = props?.currentUserIsGuest;
        const selCountry = !country
            ? countries!.find(option => option.name === "United States") || countries!.find(option => option.name === "Guam")
            : countries!.find(option => option.id === country.id);
        const [selectedCountry, setSelectedCountry] = React.useState<CountryModel | undefined>(selCountry);
        const [USflag, setUSFlag] = useState(true);
        const [states, setStates] = useState<StateModel[]>([]);
        const [isAddress2Opened, setIsAddress2Opened] = useState(false);
        const [isAddress3Opened, setIsAddress3Opened] = useState(false);
        const [isAddress4Opened, setIsAddress4Opened] = useState(false);

        useEffect(() => {
            if (country?.name === "United States") {
                setisUsStates(true);
            } else {
                setisUsStates(false);
            }
        });

        useEffect(() => {
            setSelectedCountry(selectedCountry);
            onChangeCountry({} as any, selectedCountry || null);
        }, [selectedCountry]);

        useEffect(() => {
            if (USflag) {
                setStates(rearrangeUSstate(selectedCountry) || []);
            } else {
                setStates(selectedCountry?.states || []);
            }
        }, [selectedCountry]);

        useEffect(() => {
            if (address2Error || address3Error || address4Error) {
                setIsAddress2Opened(true);
            }

            if (address3Error || address4Error) {
                setIsAddress3Opened(true);
            }

            if (address4Error) {
                setIsAddress4Opened(true);
            }
        }, [address2Error, address3Error, address4Error]);

        const handleCountryChange: SelectProps["onChange"] = event => {
            const country = countries.find(c => c.id === event.target.value);
            setUSFlag(country?.name === "United States");
            setSelectedCountry(country);
            onChangeCountry(event, country || null);
        };

        const rearrangeUSstate = function (usStateVal: CountryModel | undefined) {
            if(usStateVal?.name === "Guam"){
                return usStateVal.states
            }
            const statesStartWithP =
                usStateVal?.states &&
                usStateVal?.states.filter(val => {
                    return val.name.startsWith("P");
                });
            const rearrangedDataP = statesStartWithP && statesStartWithP?.length>0 && [statesStartWithP[1], statesStartWithP[2], statesStartWithP[0]];
            const statesEndWithO =
                usStateVal?.states &&
                usStateVal?.states.findIndex(val => {
                    return val.name.startsWith("P");
                });
            const splitStateStillO = statesEndWithO && usStateVal?.states?.slice(0, statesEndWithO);
            const splitStateAfterP =
                statesEndWithO &&
                statesStartWithP &&
                usStateVal?.states?.slice(statesEndWithO + statesStartWithP.length, usStateVal?.states.length);
            const rearrangedStates = rearrangedDataP &&
                splitStateStillO &&
                splitStateAfterP && [...splitStateStillO, ...rearrangedDataP, ...splitStateAfterP];
            return rearrangedStates;
        };

        const handleStateChange: SelectProps["onChange"] = event => {
            const state = selectedCountry?.states?.find(s => s.id === event.target.value);
            onChangeState(event, state || null);
        };
        const isStateRequired =
            selectedCountry?.abbreviation === "US" ||
            selectedCountry?.abbreviation === "CA";
        const isNorthAmericanCountry = isStateRequired;
        return (
            <GridContainer {...styles.container} data-test-selector="customerAddressFormFields">
                {fieldDisplay.firstName!.isVisible && (
                    <GridItem {...styles.firstNameGridItem}>
                        <TextField
                            {...styles.firstNameText}
                            name="firstName"
                            label={fieldDisplay.firstName!.displayName}
                            value={firstName}
                            disabled={validation.firstName!.isDisabled}
                            required={validation.firstName!.isRequired}
                            maxLength={validation.firstName!.maxLength || undefined}
                            onChange={onChangeFirstName}
                            onBlur={onBlurFirstName}
                            error={firstNameError}
                            data-test-selector="customerAddressForm_firstName"
                        />
                    </GridItem>
                )}
                {fieldDisplay.lastName!.isVisible && (
                    <GridItem {...styles.lastNameGridItem}>
                        <TextField
                            {...styles.lastNameText}
                            name="lastName"
                            label={fieldDisplay.lastName!.displayName}
                            value={lastName}
                            disabled={validation.lastName!.isDisabled}
                            required={validation.lastName!.isRequired}
                            maxLength={validation.lastName!.maxLength || undefined}
                            onChange={onChangeLastName}
                            onBlur={onBlurLastName}
                            error={lastNameError}
                            data-test-selector="customerAddressForm_lastName"
                        />
                    </GridItem>
                )}
                {fieldDisplay.companyName!.isVisible && (
                    <GridItem {...styles.companyNameGridItem}>
                        <TextField
                            {...styles.companyNameText}
                            name="companyName"
                            label={fieldDisplay.companyName!.displayName}
                            value={companyName}
                            disabled={validation.companyName!.isDisabled}
                            required={validation.companyName!.isRequired}
                            maxLength={validation.companyName!.maxLength || undefined}
                            onChange={onChangeCompanyName}
                            onBlur={onBlurCompanyName}
                            error={companyNameError}
                            data-test-selector="customerAddressForm_companyName"
                        />
                    </GridItem>
                )}
                {fieldDisplay.attention!.isVisible && (
                    <GridItem {...styles.attentionGridItem}>
                        <TextField
                            {...styles.attentionText}
                            name="attention"
                            label={fieldDisplay.attention!.displayName}
                            value={attention}
                            disabled={validation.attention!.isDisabled}
                            required={validation.attention!.isRequired}
                            maxLength={validation.attention!.maxLength || undefined}
                            onChange={onChangeAttention}
                            onBlur={onBlurAttention}
                            error={attentionError}
                            data-test-selector="customerAddressForm_attention"
                        />
                    </GridItem>
                )}
                {fieldDisplay.address1!.isVisible && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.address1GridItem }
                            : { ...styles.registeredUserAddress1GridItem })}
                    >
                        <TextField
                            {...styles.address1Text}
                            name="address1"
                            label={fieldDisplay.address1!.displayName}
                            value={address1}
                            disabled={validation.address1!.isDisabled}
                            required={validation.address1!.isRequired}
                            maxLength={validation.address1!.maxLength || undefined}
                            onChange={onChangeAddress1}
                            onBlur={onBlurAddress1}
                            error={address1Error}
                            data-test-selector="customerAddressForm_address1"
                        />
                    </GridItem>
                )}
                {fieldDisplay.address2!.isVisible && (
                    <>
                        {hideAdditionalAddresses && !isAddress2Opened && (
                            <GridItem {...styles.addAddressLineLinkGridItem}>
                                <Link
                                {...styles.addAddressLineLink}
                                onClick={() => setIsAddress2Opened(true)}
                                data-test-selector="customerAddressForm_addressLine2"
                            >
                                    {translate("+ Address line 2")}
                                </Link>
                            </GridItem>
                        )}
                        {(!hideAdditionalAddresses || isAddress2Opened) && (
                            <GridItem {...styles.address2GridItem}>
                                <TextField
                                    {...styles.address2Text}
                                    name="address2"
                                    label={fieldDisplay.address2!.displayName}
                                    value={address2}
                                    disabled={validation.address2!.isDisabled}
                                    required={validation.address2!.isRequired}
                                    maxLength={validation.address2!.maxLength || undefined}
                                    onChange={onChangeAddress2}
                                    error={address2Error}
                                    data-test-selector="customerAddressForm_address2"
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {fieldDisplay.address3!.isVisible && (
                    <>
                        {hideAdditionalAddresses && isAddress2Opened && !isAddress3Opened && (
                            <GridItem {...styles.addAddressLineLinkGridItem}>
                                <Link {...styles.addAddressLineLink} onClick={() => setIsAddress3Opened(true)}>
                                    {translate("+ Address line 3")}
                                </Link>
                            </GridItem>
                        )}
                        {(!hideAdditionalAddresses || isAddress3Opened) && (
                            <GridItem {...styles.address3GridItem}>
                                <TextField
                                    {...styles.address3Text}
                                    name="address3"
                                    label={fieldDisplay.address3!.displayName}
                                    value={address3}
                                    disabled={validation.address3!.isDisabled}
                                    required={validation.address3!.isRequired}
                                    maxLength={validation.address3!.maxLength || undefined}
                                    onChange={onChangeAddress3}
                                    error={address3Error}
                                    data-test-selector="customerAddressForm_address3"
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {fieldDisplay.address4!.isVisible && (
                    <>
                        {hideAdditionalAddresses && isAddress3Opened && !isAddress4Opened && (
                            <GridItem {...styles.addAddressLineLinkGridItem}>
                                <Link {...styles.addAddressLineLink} onClick={() => setIsAddress4Opened(true)}>
                                    {translate("+ Address line 4")}
                                </Link>
                            </GridItem>
                        )}
                        {(!hideAdditionalAddresses || isAddress4Opened) && (
                            <GridItem {...styles.address4GridItem}>
                                <TextField
                                    {...styles.address4Text}
                                    name="address4"
                                    label={fieldDisplay.address4!.displayName}
                                    value={address4}
                                    disabled={validation.address4!.isDisabled}
                                    required={validation.address4!.isRequired}
                                    maxLength={validation.address4!.maxLength || undefined}
                                    onChange={onChangeAddress4}
                                    error={address4Error}
                                    data-test-selector="customerAddressForm_address4"
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {fieldDisplay.city!.isVisible && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.cityGridItem }
                            : { ...styles.registeredUserCityGridItem })}
                    >
                        <TextField
                            {...styles.cityText}
                            name="city"
                            label={fieldDisplay.city!.displayName}
                            value={city}
                            disabled={validation.city!.isDisabled}
                            required={validation.city!.isRequired}
                            maxLength={validation.city!.maxLength || undefined}
                            onChange={onChangeCity}
                            onBlur={onBlurCity}
                            error={cityError}
                            data-test-selector="customerAddressForm_city"
                        />
                    </GridItem>
                )}
                {isUsStates ? (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.stateGridItem }
                            : { ...styles.registeredUserStateGridItem })}
                    >
                        <Select
                            {...styles.stateSelect}
                            label={fieldDisplay.state!.displayName}
                            value={state?.id || ""}
                            disabled={validation.state!.isDisabled}
                            required={isStateRequired}
                            onChange={handleStateChange}
                            error={stateError}
                            data-test-selector="customerAddressForm_state-US"
                        >
                            <option value="">{translate("Select State")}</option>
                            {states && states?.filter(Boolean).map(st => (
                                <option key={st.id} value={st.id}>
                                    {st.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                ) : (
                    <>
                        <GridItem
                            {...(currentUserIsGuest
                                ? { ...styles.stateGridItem }
                                : { ...styles.registeredUserStateGridItem })}
                        >
                            <Select
                                {...styles.stateSelect}
                                label={fieldDisplay.state!.displayName}
                                value={state?.id || ""}
                                disabled={validation.state!.isDisabled}
                                required={isStateRequired}
                                onChange={handleStateChange}
                                data-test-selector="customerAddressForm_state"
                            >
                                <option value="">{translate("Select State")}</option>
                                {states && states?.filter(Boolean).map(st => (
                                    <option key={st.id} value={st.id}>
                                        {st.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    </>
                )}
                {fieldDisplay.postalCode!.isVisible && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.postalCodeGridItem }
                            : { ...styles.registeredUserPostalCodeGridItem })}
                    >
                        <TextField
                            {...styles.postalCodeText}
                            name="postalCode"
                            label={fieldDisplay.postalCode!.displayName}
                            value={postalCode}
                            disabled={validation.postalCode!.isDisabled}
                            required={validation.postalCode!.isRequired}
                            maxLength={validation.postalCode!.maxLength || undefined}
                            onChange={onChangePostalCode}
                            onBlur={onBlurPostalCode}
                            error={postalCodeError}
                            data-test-selector="customerAddressForm_postalCode"
                        />
                    </GridItem>
                )}
                {fieldDisplay.phone!.isVisible && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.phoneGridItem }
                            : { ...styles.registeredUserPhoneGridItem })}
                    >
                        <TextField
                            {...styles.phoneText}
                            name="phone"
                            autoComplete="autocomplete_off_randString"
                            label={fieldDisplay.phone!.displayName}
                            value={phone}
                            disabled={validation.phone!.isDisabled}
                            required={validation.phone!.isRequired}
                            maxLength={isNorthAmericanCountry ? 12 : undefined}
                            onChange={onChangePhone}
                            onBlur={onBlurPhone}
                            error={isNorthAmericanCountry ? phoneError : undefined}
                            data-test-selector="customerAddressForm_phone"
                        />
                    </GridItem>
                )}
                {fieldDisplay.country!.isVisible && countries.length > 1 && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.countryGridItem }
                            : { ...styles.registeredUserCountryGridItem })}
                    >
                        <Select
                            {...styles.countrySelect}
                            label={fieldDisplay.country!.displayName}
                            value={selectedCountry?.id || ""}
                            disabled={validation.country!.isDisabled || countries.length === 1}
                            required={validation.country!.isRequired}
                            onChange={handleCountryChange}
                            error={countryError}
                            data-test-selector="customerAddressForm_country_Test"
                        >
                            {/* <option value="">{translate("Select Country")}</option> */}
                            {countries.map(co => (
                                <option key={co.id} value={co.id}>
                                    {co.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                )}
                {fieldDisplay.fax!.isVisible && (
                    <GridItem {...styles.faxGridItem}>
                        <TextField
                            {...styles.faxText}
                            name="fax"
                            label={fieldDisplay.fax!.displayName}
                            value={fax}
                            disabled={validation.fax!.isDisabled}
                            required={validation.fax!.isRequired}
                            maxLength={validation.fax!.maxLength || undefined}
                            onChange={onChangeFax}
                            onBlur={onBlurFax}
                            error={faxError}
                            data-test-selector="customerAddressForm_fax"
                        />
                    </GridItem>
                )}
                {fieldDisplay.email!.isVisible && (
                    <GridItem
                        {...(currentUserIsGuest
                            ? { ...styles.emailGridItem }
                            : { ...styles.registeredUserEmailGridItem })}
                    >
                        <TextField
                            {...styles.emailText}
                            name="email"
                            label={
                                isShippingAddress ? (
                                    <>
                                        {fieldDisplay.email!.displayName}{' '}
                                        <span   style={{ fontSize: 14 }}>(for tracking and invoice)</span>
                                    </>
                                ) : (
                                    fieldDisplay.email!.displayName
                                )
                            }
                            value={email}
                            disabled={validation.email!.isDisabled}
                            required={validation.email!.isRequired}
                            maxLength={validation.email!.maxLength || undefined}
                            onChange={onChangeEmail}
                            onBlur={onBlurEmail}
                            error={emailError}
                            data-test-selector="customerAddressForm_email"
                        />
                    </GridItem>
                )}
            </GridContainer>
        );
    };
export default CustomerAddressFormFields;
